import React, { useState, useEffect } from 'react';
import { shopService, Shop, ShopProduct } from '../services/shopService';
import { apiService } from '../services/api';
import { getValidImageUrl } from '../utils/imageUtils';
import { useCategoryTranslation, useShopTypeTranslation } from '../utils/categoryTranslations';
import { useTranslations } from '../hooks/useTranslations';
import './MultiShopBrowser.css';

interface MultiShopBrowserProps {
  onAddToCart: (product: ShopProduct, shop: Shop) => void;
  onViewShop: (shop: Shop) => void;
  cartItems?: Array<{ product: ShopProduct; shop: Shop; quantity: number }>;
  initialCategoryId?: string;
  initialCategoryName?: string;
  initialShopId?: string;
}

const MultiShopBrowser: React.FC<MultiShopBrowserProps> = ({ 
  onAddToCart, 
  onViewShop, 
  cartItems = [],
  initialCategoryId,
  initialCategoryName: _initialCategoryName,
  initialShopId
}) => {
  const translateCategoryName = useCategoryTranslation();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const translateShopType = useShopTypeTranslation();
  const t = useTranslations();
  const [shops, setShops] = useState<Shop[]>([]);
  const [filteredShops, setFilteredShops] = useState<Shop[]>([]);
  const [selectedShop, setSelectedShop] = useState<Shop | null>(null);
  const [products, setProducts] = useState<ShopProduct[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(initialCategoryId || 'all');
  const [selectedBrand, setSelectedBrand] = useState('all');
  const [selectedShopType, setSelectedShopType] = useState('all');
  const [isLoading, setIsLoading] = useState(false);
  const [searchResults, setSearchResults] = useState<{ shop: Shop; products: ShopProduct[] }[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isSearching, setIsSearching] = useState(false);


  // Check if a product is already in the cart
  const isProductInCart = (product: ShopProduct, shop: Shop): boolean => {
    return cartItems.some(item => 
      item.product.id === product.id && item.shop.id === shop.id
    );
  };

  // Load all shops on component mount
  useEffect(() => {
    const loadShops = async () => {
      try {
        // Try API first
        try {
          const apiShops = await apiService.getShops();
          if (apiShops && apiShops.length > 0) {
            setShops(apiShops);
            setFilteredShops(apiShops);
            // Select shop by initialShopId if provided, otherwise select first shop
            if (initialShopId) {
              const shop = apiShops.find(s => s.id === initialShopId);
              if (shop) {
                setSelectedShop(shop);
              } else if (apiShops.length > 0) {
                setSelectedShop(apiShops[0]);
              }
            } else if (apiShops.length > 0) {
              setSelectedShop(apiShops[0]);
            }
            return;
          }
        } catch (apiError) {
          console.warn('API shops not available, using shopService:', apiError);
        }
        
        // Fallback to shopService
        const allShops = await shopService.getShops();
        setShops(allShops);
        setFilteredShops(allShops);
        // Select shop by initialShopId if provided, otherwise select first shop
        if (initialShopId) {
          const shop = allShops.find(s => s.id === initialShopId);
          if (shop) {
            setSelectedShop(shop);
          } else if (allShops.length > 0) {
            setSelectedShop(allShops[0]);
          }
        } else if (allShops.length > 0) {
          setSelectedShop(allShops[0]);
        }
      } catch (error) {
        console.error('Error loading shops:', error);
      }
    };

    loadShops();
  }, [initialShopId]);

  // Filter shops based on shop type (similar to ShopDiscovery)
  useEffect(() => {
    let filtered = shops;

    // Filter by shop type
    if (selectedShopType !== 'all') {
      filtered = filtered.filter(shop => shop.type === selectedShopType);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      filtered = filtered.filter(shop =>
        shop.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        shop.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        shop.address.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredShops(filtered);
    
    // Update selected shop if current selection is not in filtered results
    if (selectedShop && !filtered.find(shop => shop.id === selectedShop.id)) {
      if (filtered.length > 0) {
        setSelectedShop(filtered[0]);
      } else {
        setSelectedShop(null);
      }
    }
  }, [shops, selectedShopType, searchQuery, selectedShop]);

  // Load products when shop changes
  useEffect(() => {
    const loadShopProducts = async () => {
      if (selectedShop) {
        setIsLoading(true);
        try {
          // Try API first - use category ID if available, otherwise use category name
          try {
            const categoryParam = selectedCategory !== 'all' 
              ? (initialCategoryId || selectedCategory)
              : undefined;
            
            const apiProducts = await apiService.getShopProducts(selectedShop.id, {
              category: categoryParam,
              search: searchQuery || undefined
            });
            if (apiProducts && apiProducts.length > 0) {
              const convertedProducts = apiProducts.map(product => ({
                ...product,
                shopId: (product as any).shop_id || (product as any).shopId,
                category_id: product.category_id,
                stock: (product as any).stock_quantity || 0,
                isActive: product.is_active !== undefined ? product.is_active : true,
                createdAt: product.created_at || new Date().toISOString(),
                updatedAt: product.updated_at || new Date().toISOString()
              } as ShopProduct));
              setProducts(convertedProducts);
              return;
            }
          } catch (apiError) {
            console.warn('API products not available, using shopService:', apiError);
          }
          
          // Fallback to shopService
          const shopProducts = await shopService.getShopProducts(selectedShop.id);
          setProducts(shopProducts);
        } catch (error) {
          console.error('Error loading shop products:', error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    loadShopProducts();
  }, [selectedShop, selectedCategory, selectedBrand, searchQuery, initialCategoryId]);

  // Handle search across all shops
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      // Try API first
      try {
        const results = await apiService.searchAllShops(searchQuery, {
          shopType: selectedShopType !== 'all' ? selectedShopType : undefined,
          category: selectedCategory !== 'all' ? selectedCategory : undefined
        });
        const convertedResults = results.map(result => ({
          shop: result.shop,
          products: result.products.map(product => ({
            ...product,
            shopId: (product as any).shop_id || (product as any).shopId,
            category_id: product.category_id,
            stock: (product as any).stock_quantity || 0,
            isActive: product.is_active !== undefined ? product.is_active : true,
            createdAt: product.created_at || new Date().toISOString(),
            updatedAt: product.updated_at || new Date().toISOString()
          } as ShopProduct))
        }));
        setSearchResults(convertedResults);
        return;
      } catch (apiError) {
        console.warn('API search not available, using shopService:', apiError);
      }
      
      // Fallback to shopService
      const results = await shopService.searchAllShops(searchQuery, {
        shopType: selectedShopType !== 'all' ? selectedShopType : undefined,
        category: selectedCategory !== 'all' ? selectedCategory : undefined
      });
      setSearchResults(results);
    } catch (error) {
      console.error('Error searching products:', error);
    } finally {
      setIsSearching(false);
    }
  };

  // Filter products by category, brand, and search
  const filteredProducts = products.filter(product => {
    const matchesCategory = selectedCategory === 'all' || product.category === selectedCategory;
    const matchesBrand = selectedBrand === 'all' || product.brand === selectedBrand;
    const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (product.brand && product.brand.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesCategory && matchesBrand && matchesSearch;
  });

  // Get unique categories from current shop products
  const getCategories = () => {
    const categories = new Set(products.map(product => product.category).filter(cat => cat != null && cat !== ''));
    return Array.from(categories).map(category => ({
      id: category || 'unknown',
      name: category ? category.replace('-', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()) : 'Unknown'
    }));
  };

  // Get unique brands from current shop products
  const getBrands = () => {
    const brands = new Set(products.map(product => product.brand).filter(brand => brand && brand.trim() !== ''));
    return Array.from(brands).map(brand => ({
      id: brand || 'unknown',
      name: brand || 'Unknown Brand'
    }));
  };

  // Get shop type icon (same as ShopDiscovery)
  const getShopTypeIcon = (type: string) => {
    switch (type) {
      case 'medical': return '🏥';
      case 'grocery': return '🥬';
      case 'electronics': return '📱';
      case 'clothing': return '👕';
      case 'pharmacy': return '💊';
      case 'bakery': return '🥖';
      case 'restaurant': return '🍛';
      case 'specialty': return '⭐';
      default: return '🏪';
    }
  };

  // Get unique shop types with icons (same as ShopDiscovery)
  const getShopTypes = () => {
    const types = new Set(shops.map(shop => shop.type));
    return Array.from(types).map(type => ({
      id: type,
      name: type.charAt(0).toUpperCase() + type.slice(1),
      icon: getShopTypeIcon(type),
      count: shops.filter(shop => shop.type === type).length
    }));
  };

  const categories = getCategories();
  const brands = getBrands();
  const shopTypes = getShopTypes();

  return (
    <div className="multi-shop-browser">
      {/* Header with Quick Stats */}
      <div className="browser-header">
        <div className="header-content">
          <div className="header-text">
            <h2>🛍️ Browse All Shops</h2>
            <p>Discover products from our network of specialized shops</p>
          </div>
        </div>
      </div>

      {/* Combined Search and Filter Bar */}
      <div className="search-filter-bar">
        <div className="search-container">
          <input
            type="text"
            placeholder="Search shops by name, description, or location..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
          <button className="search-btn">🔍</button>
        </div>
        
        <div className="filter-container">
          <div className="filter-chips">
            <button
              className={`filter-chip ${selectedShopType === 'all' ? 'active' : ''}`}
              onClick={() => setSelectedShopType('all')}
            >
              All ({shops.length})
            </button>
            {shopTypes.map(type => (
              <button
                key={type.id}
                className={`filter-chip ${selectedShopType === type.id ? 'active' : ''}`}
                onClick={() => setSelectedShopType(type.id)}
              >
                {type.icon} {type.name} ({type.count})
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Search Results */}
      {searchResults.length > 0 && (
        <div className="search-results">
          <h3>Search Results</h3>
          {searchResults.map(({ shop, products: searchProducts }) => (
            <div key={shop.id} className="search-shop-section">
              <div className="shop-header" onClick={() => onViewShop(shop)}>
                <h4>
                  {shop.type === 'medical' ? '🏥' : 
                   shop.type === 'bakery' ? '🥖' : 
                   shop.type === 'restaurant' ? '🍛' : '🏪'} {shop.name}
                </h4>
                <p>{shop.description}</p>
                <span className="shop-type-badge">{shop.type}</span>
              </div>
              <div className="search-products-grid">
                {searchProducts.map(product => (
                  <div key={product.id} className="product-card">
                    <div className="product-image">
                      <img 
                        src={getValidImageUrl(product.image)}
                        alt={product.name}
                        onError={(e) => {
                          console.log('🌐 MultiShopBrowser - Image failed to load:', product.image);
                          e.currentTarget.src = 'https://via.placeholder.com/150x150/667eea/ffffff?text=No+Image';
                        }}
                        onLoad={() => console.log('🌐 MultiShopBrowser - Image loaded successfully:', product.image)}
                      />
                    </div>
                    <div className="product-info">
                      <h5>{product.name}</h5>
                      <p className="product-price">${product.price.toFixed(2)}</p>
                      <p className="product-brand">{product.brand}</p>
                      <button
                        onClick={() => onAddToCart(product, shop)}
                        className={`add-to-cart-btn ${isProductInCart(product, shop) ? 'disabled' : ''}`}
                        disabled={isProductInCart(product, shop)}
                      >
                        {isProductInCart(product, shop) ? 'In Cart' : 'Add to Cart'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Shop Selection */}
      {!searchResults.length && (
        <div className="shops-section">
          <div className="section-header">
            <h3>
              {selectedShopType === 'all' ? 'All Shops' : `${shopTypes.find(t => t.id === selectedShopType)?.name} Shops`}
            </h3>
            <span className="results-count">
              {filteredShops.length} {filteredShops.length === 1 ? 'shop' : 'shops'} found
            </span>
          </div>

          {filteredShops.length === 0 ? (
            <div className="no-shops">
              <div className="no-shops-icon">🔍</div>
              <h4>No shops found</h4>
              <p>Try adjusting your search criteria or browse all shops</p>
              <button onClick={() => {
                setSearchQuery('');
                setSelectedShopType('all');
              }} className="clear-filters-btn">
                Clear Filters
              </button>
            </div>
          ) : (
            <div className="shops-grid">
              {filteredShops.map(shop => (
                <div
                  key={shop.id}
                  className={`shop-card ${selectedShop?.id === shop.id ? 'selected' : ''}`}
                  onClick={() => setSelectedShop(shop)}
                >
                  <div className="shop-card-image">
                    {getShopTypeIcon(shop.type)}
                  </div>
                  
                  <div className="shop-card-content">
                    <div className="shop-card-header">
                      <h4 className="shop-card-title">{shop.name}</h4>
                      <div className="shop-status">
                        {shop.is_active ? (
                          <span className="status-active">🟢 {t.open || 'Open'}</span>
                        ) : (
                          <span className="status-inactive">🔴 {t.closed || 'Closed'}</span>
                        )}
                      </div>
                    </div>
                    
                    <p className="shop-card-description">{shop.description}</p>
                    
                    <div className="shop-card-meta">
                      <div className="shop-rating">
                        ⭐⭐⭐⭐⭐ (4.8)
                      </div>
                      <div className="shop-category">
                        {shop.type}
                      </div>
                    </div>
                    
                    <div className="shop-location">
                      📍 {shop.city}, {shop.state}
                    </div>
                  </div>

                  <div className="shop-card-actions">
                    {/* Primary Action: Browse Products */}
                    <button
                      onClick={() => setSelectedShop(shop)}
                      className="visit-shop-btn primary-action"
                      disabled={!shop.is_active}
                      title={shop.is_active ? 'View and shop products from this store' : 'Shop is currently closed'}
                    >
                      🛒 {shop.is_active ? (t.browseProducts || 'Browse Products') : (t.temporarilyClosed || 'Temporarily Closed')}
                    </button>
                    {/* Secondary Action: Shop Info */}
                    <button
                      onClick={() => onViewShop(shop)}
                      className="view-details-btn secondary-action"
                      title="View shop information (location, hours, contact, rating)"
                    >
                      ℹ️ {t.shopInfo || 'Shop Info'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Products from Selected Shop */}
      {!searchResults.length && selectedShop && (
        <div className="shop-products">
          <div className="shop-header">
            <h3>
              {selectedShop.type === 'medical' ? '🏥' : 
               selectedShop.type === 'bakery' ? '🥖' : 
               selectedShop.type === 'restaurant' ? '🍛' : '🏪'} {selectedShop.name}
            </h3>
            <p>{selectedShop.description}</p>
            <button onClick={() => onViewShop(selectedShop)} className="view-shop-btn">
              {t.viewDetails || 'View Shop Details'}
            </button>
          </div>

          {/* Category Filter */}
          <div className="category-filter">
            <h4>Categories:</h4>
            <div className="filter-buttons">
              <button
                className={`category-btn ${selectedCategory === 'all' ? 'active' : ''}`}
                onClick={() => setSelectedCategory('all')}
              >
                All Products
              </button>
              {categories.map(category => (
                <button
                  key={category.id}
                  className={`category-btn ${selectedCategory === category.id ? 'active' : ''}`}
                  onClick={() => setSelectedCategory(category.id)}
                >
                  {translateCategoryName(category.name)}
                </button>
              ))}
            </div>
          </div>

          {/* Brand Filter */}
          {brands.length > 0 && (
            <div className="brand-filter">
              <h4>Brands:</h4>
              <div className="filter-buttons">
                <button
                  className={`brand-btn ${selectedBrand === 'all' ? 'active' : ''}`}
                  onClick={() => setSelectedBrand('all')}
                >
                  All Brands
                </button>
                {brands.map(brand => (
                  <button
                    key={brand.id}
                    className={`brand-btn ${selectedBrand === brand.id ? 'active' : ''}`}
                    onClick={() => setSelectedBrand(brand.id)}
                  >
                    {brand.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Products Grid */}
          {isLoading ? (
            <div className="loading">Loading products...</div>
          ) : (
            <div className="products-grid">
              {filteredProducts.map(product => (
                <div key={product.id} className="product-card">
                  <div className="product-image">
                    <img 
                      src={getValidImageUrl(product.image)}
                      alt={product.name}
                      onError={(e) => {
                        console.log('🌐 MultiShopBrowser - Image failed to load:', product.image);
                        e.currentTarget.src = 'https://via.placeholder.com/150x150/667eea/ffffff?text=No+Image';
                      }}
                      onLoad={() => console.log('🌐 MultiShopBrowser - Image loaded successfully:', product.image)}
                    />
                  </div>
                  <div className="product-info">
                    <h5>{product.name}</h5>
                    <p className="product-price">${product.price.toFixed(2)}</p>
                    <p className="product-brand">{product.brand}</p>
                    <p className="product-description">{product.description}</p>
                    <div className="product-stock">
                      {product.inStock ? (
                        <span className="in-stock">✅ In Stock ({product.stock})</span>
                      ) : (
                        <span className="out-of-stock">❌ Out of Stock</span>
                      )}
                    </div>
                    <button
                      onClick={() => onAddToCart(product, selectedShop)}
                      className={`add-to-cart-btn ${isProductInCart(product, selectedShop) ? 'disabled' : ''}`}
                      disabled={!product.inStock || isProductInCart(product, selectedShop)}
                    >
                      {!product.inStock ? 'Out of Stock' : 
                       isProductInCart(product, selectedShop) ? 'In Cart' : 'Add to Cart'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {filteredProducts.length === 0 && !isLoading && (
            <div className="no-products">
              <p>No products found matching your filters.</p>
              <button 
                onClick={() => {
                  setSelectedCategory('all');
                  setSelectedBrand('all');
                  setSearchQuery('');
                }} 
                className="clear-filters-btn"
              >
                Clear All Filters
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MultiShopBrowser;
